import { z } from 'zod'

/**
 * Canonical OAuth scope list — PRD B8.1 / B21.6.
 * Slash notation in the PRD denotes two distinct literal scopes (read/write pairs).
 * Single source for OAuth policy and MCP tool manifests.
 */

export const MCP_SCOPES = [
  'profile.read',
  'agreements.read',
  'agreements.write',
  'workspace.read',
  'workspace.write',
  'templates.read',
  'templates.write',
  'content.read',
  'contacts.read',
  'contacts.write',
  'analytics.read',
  'delivery.write',
  'evidence.read',
  'billing.read',
  'purchase.start',
  'account.manage',
] as const

/**
 * What a host gets when it asks for no scope (advisor, 27 Oct: real hosts send none). Every scope
 * except billing and analytics; the consent page lists each one before anything is granted. It is
 * also what the 401 challenge and the protected-resource metadata advertise.
 */
export const MCP_DEFAULT_SCOPES = MCP_SCOPES.filter(
  (scope) => scope !== 'billing.read' && scope !== 'purchase.start' && scope !== 'analytics.read',
)

export const McpScope = z.enum(MCP_SCOPES)
export type McpScope = z.infer<typeof McpScope>

export type ScopeName = (typeof MCP_SCOPES)[number]

/** Public tools require no OAuth scope (B8.1 "No OAuth (public tools)"). */
export const PUBLIC_TOOL_SCOPE = null

export function isMcpScope(value: unknown): value is McpScope {
  return typeof value === 'string' && (MCP_SCOPES as readonly string[]).includes(value)
}

export function hasScope(granted: readonly string[], required: McpScope): boolean {
  return granted.includes(required)
}
